/**
 * HelpButton — the "?" entry point for the help system. Opens a tiny menu:
 *   • Show / hide help labels  → toggles helpStore.helpMode (callouts + hover cards)
 *   • Take the guided tour     → starts the spotlight walkthrough
 *
 * Lives in the TopBar next to Settings. Copy comes from helpContent (config).
 */
import { useState } from 'react';
import { HelpCircle, BookOpen, Tags } from 'lucide-react';
import { useHelpStore } from '@/stores/helpStore';
import { tip } from '@/config/helpContent';

export function HelpButton() {
  const [open, setOpen] = useState(false);
  const helpMode = useHelpStore((s) => s.helpMode);
  const toggleHelpMode = useHelpStore((s) => s.toggleHelpMode);
  const startTour = useHelpStore((s) => s.startTour);

  return (
    <div className="relative">
      <button
        data-testid="help-button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Help"
        aria-expanded={open}
        title={tip('helpButton')}
        className="flex items-center justify-center p-1.5 rounded text-vibe-text-secondary hover:text-vibe-text hover:bg-vibe-bg-tertiary transition-colors [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
      >
        <HelpCircle className="w-4 h-4" />
      </button>

      {open && (
        <>
          {/* click-away */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            data-testid="help-menu"
            className="absolute right-0 mt-1 z-50 w-56 bg-vibe-bg-secondary border border-vibe-border rounded shadow-xl py-1 text-sm"
          >
            <button
              data-testid="help-menu-toggle-labels"
              onClick={() => {
                toggleHelpMode();
                setOpen(false);
              }}
              className="flex items-center gap-2 w-full px-3 py-2 text-left text-vibe-text hover:bg-vibe-bg-tertiary"
            >
              <Tags className="w-4 h-4 flex-shrink-0" />
              {helpMode ? 'Hide help labels' : 'Show help labels'}
            </button>
            <button
              data-testid="help-menu-start-tour"
              onClick={() => {
                startTour();
                setOpen(false);
              }}
              className="flex items-center gap-2 w-full px-3 py-2 text-left text-vibe-text hover:bg-vibe-bg-tertiary"
            >
              <BookOpen className="w-4 h-4 flex-shrink-0" />
              Take the guided tour
            </button>
          </div>
        </>
      )}
    </div>
  );
}
